import { OVERLAY_GAP } from './constants';
import { getMaxRange } from './get-max-range';
import { floorVideoHeight } from './round-video-size';
import { Area, Size } from './types';

export const OLD_PARTICIPANT_SIZES = {
    XS_WIDTH: 120,
    S_WIDTH: 144,
    HEIGHT: 112,
};

export const NEW_PARTICIPANT_SIZES = {
    S_WIDTH: 192,
    XS_WIDTH: 192,
    S_HEIGHT: 108,
    XS_HEIGHT: 108,
};

const WINDOW_THRESHOLD_WIDTH = 952;
const DEFAULT_RATIO = 16 / 9;

function oldGetPresenterLayout(size: Size, count: number, shouldSwapSelfView?: boolean): Area[] {
    if (!size.width || !size.height || count < 1) {
        return [];
    }

    if (count === 1) {
        const videoWidth = Math.min(size.width, size.height * DEFAULT_RATIO);
        const videoHeight = videoWidth / DEFAULT_RATIO;

        return [
            {
                x: Math.max((size.width - videoWidth) / 2, 0),
                y: Math.max((size.height - videoHeight) / 2, 0),
                width: videoWidth,
                height: videoHeight,
            },
        ];
    }

    const presenterAreas: Area[] = [
        {
            width: size.width,
            height: size.height - OLD_PARTICIPANT_SIZES.HEIGHT,
            x: 0,
            y: OLD_PARTICIPANT_SIZES.HEIGHT,
        },
    ];

    const countInRow = count - 1;
    const itemWidth =
        window.innerWidth < WINDOW_THRESHOLD_WIDTH ? OLD_PARTICIPANT_SIZES.XS_WIDTH : OLD_PARTICIPANT_SIZES.S_WIDTH;
    const sumWidth = itemWidth * countInRow;
    const startPoint = (size.width - sumWidth) / 2;

    for (let i = 0; i < countInRow; i++) {
        presenterAreas.push({
            x: startPoint + i * itemWidth,
            y: 0,
            width: itemWidth,
            height: OLD_PARTICIPANT_SIZES.HEIGHT,
        });
    }

    if (shouldSwapSelfView) {
        const selfView = presenterAreas[0];
        presenterAreas[0] = presenterAreas[1];
        presenterAreas[1] = selfView;
    }

    return presenterAreas;
}

export const calcPresenterArea = (
    size: Size,
    isSharingActive: boolean,
    offsets: { x?: number; y?: number } = { x: 0, y: 0 }
): Area => {
    const { width, height } = size;
    if (isSharingActive) {
        return {
            width: width,
            height: height,
            x: offsets.x ?? 0,
            y: offsets.y ?? 0,
        };
    }
    const bestVideoHeight = Math.min(height, width / DEFAULT_RATIO);
    const { min: minHeight, max: maxHeight } = getMaxRange(bestVideoHeight);
    const videoHeight = bestVideoHeight < minHeight ? bestVideoHeight : Math.min(bestVideoHeight, maxHeight);
    const roundedVideoHeight = floorVideoHeight(videoHeight);
    const videoWidth = roundedVideoHeight * DEFAULT_RATIO;

    return {
        width: videoWidth,
        height: roundedVideoHeight,
        x: (size.width - videoWidth) / 2 + (offsets.x ?? 0),
        y: (size.height - roundedVideoHeight) / 2 + (offsets.y ?? 0),
    };
};

type newGetPresenterLayoutParams = {
    size: Size;
    count: number;
    shouldSwapSelfView?: boolean;
    isMobile?: boolean;
    isScreenSharingActive?: boolean;
    margin?: number;
};
function newGetPresenterLayout({
    size,
    count,
    shouldSwapSelfView,
    isMobile,
    isScreenSharingActive,
    margin = 0,
}: newGetPresenterLayoutParams): Area[] {
    if (!size.width || !size.height || count < 1) {
        return [];
    }
    const PARTICIPANT_HEIGHT = isMobile ? NEW_PARTICIPANT_SIZES.XS_HEIGHT : NEW_PARTICIPANT_SIZES.S_HEIGHT;
    const PARTICIPANT_WIDTH = isMobile ? NEW_PARTICIPANT_SIZES.XS_WIDTH : NEW_PARTICIPANT_SIZES.S_WIDTH;
    const containerSize = {
        width: size.width - margin * 2,
        height: size.height - margin * 2,
    };
    const mainPresenterSize: Size = {
        width: containerSize.width,
        height: containerSize.height - PARTICIPANT_HEIGHT - OVERLAY_GAP,
    };
    if (count === 1) {
        const videoWidth = Math.min(size.width, size.height * DEFAULT_RATIO);
        const videoHeight = videoWidth / DEFAULT_RATIO;

        return [
            {
                x: Math.max((size.width - videoWidth) / 2, 0),
                y: Math.max((size.height - videoHeight) / 2, 0),
                width: videoWidth,
                height: videoHeight,
            },
        ];
    }
    const mainPresenterArea = calcPresenterArea(mainPresenterSize, isScreenSharingActive ?? false, {
        y: PARTICIPANT_HEIGHT + OVERLAY_GAP + margin,
        x: margin,
    });

    const presenterAreas: Area[] = [mainPresenterArea];

    const countInRow = count - 1;
    const itemWidth = PARTICIPANT_WIDTH;
    const sumWidth = itemWidth * countInRow + OVERLAY_GAP * (countInRow - 1);
    const startPoint = (size.width - sumWidth) / 2;

    for (let i = 0; i < countInRow; i++) {
        presenterAreas.push({
            x: startPoint + i * itemWidth + OVERLAY_GAP * i,
            y: margin,
            width: itemWidth,
            height: PARTICIPANT_HEIGHT,
        });
    }

    if (shouldSwapSelfView) {
        const selfView = presenterAreas[0];
        presenterAreas[0] = presenterAreas[1];
        presenterAreas[1] = selfView;
    }

    return presenterAreas;
}

type GetPresenterLayoutParams = {
    size: Size;
    count: number;
    shouldSwapSelfView?: boolean;
    isMobile?: boolean;
    isScreenSharingActive?: boolean;
    isNewGrid?: boolean;
    margin?: number;
};
export function getPresenterLayout({
    size,
    count,
    shouldSwapSelfView,
    isScreenSharingActive,
    isMobile,
    isNewGrid = false,
    margin = 0,
}: GetPresenterLayoutParams): Area[] {
    return isNewGrid
        ? newGetPresenterLayout({
              size,
              count,
              shouldSwapSelfView,
              isScreenSharingActive,
              isMobile,
              margin,
          })
        : oldGetPresenterLayout(size, count, shouldSwapSelfView);
}
